import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Trophy } from 'lucide-react';
import { SCORE_LABELS, SCORE_POINTS, type GameKind, type RatingRow, type ScoreReason } from '@mafia/core';
import { KindTabs } from '@/components/Kind';
import { api } from '@/lib/api';
import { useAuth } from '@/store/auth';
import { Avatar } from '@/components/Avatar';
import { Alert, Card, Empty, PageSpinner } from '@/components/ui';

export function RatingPage() {
  const me = useAuth((s) => s.user)!;
  const [kind, setKind] = useState<GameKind>('AMATEUR');
  // Любительский и турнирный рейтинги считаются отдельно.
  const rating = useQuery({ queryKey: ['rating', kind], queryFn: () => api<RatingRow[]>(`/api/rating?kind=${kind}`) });

  return (
    <div className="mx-auto grid max-w-5xl gap-4 px-4 py-6 sm:py-10 lg:grid-cols-[1fr_300px]">
      <div>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h1 className="flex items-center gap-2 font-display text-3xl">
            <Trophy className="h-7 w-7 text-gold-400" /> Рейтинг
          </h1>
          <KindTabs value={kind} onChange={(k) => setKind(k)} />
        </div>
        {rating.isLoading ? (
          <PageSpinner />
        ) : rating.isError ? (
          <Alert>Не удалось загрузить рейтинг</Alert>
        ) : rating.data!.length === 0 ? (
          <Card>
            <Empty icon="🏆" title={kind === 'TOURNAMENT' ? 'Турнирных игр ещё не было' : 'Пока никто не набрал баллов'}>
              {kind === 'TOURNAMENT'
                ? 'Турнирные столы создают администраторы; баллы за них идут в отдельный рейтинг.'
                : 'Сыграйте первую партию — баллы начисляются по её итогам.'}
            </Empty>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <table className="table-base">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Игрок</th>
                  <th className="text-right">Игр</th>
                  <th className="text-right">Побед</th>
                  <th className="text-right">Баллы</th>
                </tr>
              </thead>
              <tbody>
                {rating.data!.map((r) => (
                  <tr key={r.userId} className={clsx(r.userId === me.id && 'bg-gold-400/5')}>
                    <td className={clsx('font-mono', r.rank <= 3 ? 'text-gold-300' : 'text-zinc-500')}>
                      {r.rank <= 3 ? ['🥇', '🥈', '🥉'][r.rank - 1] : r.rank}
                    </td>
                    <td>
                      <Link to={`/profile/${r.userId}`} className="flex items-center gap-2 hover:text-gold-300">
                        <Avatar id={r.avatarId} name={r.nickname} size={28} />
                        {r.nickname}
                      </Link>
                    </td>
                    <td className="text-right tabular-nums text-zinc-400">{r.played}</td>
                    <td className="text-right tabular-nums text-zinc-400">
                      {r.won} <span className="text-zinc-600">({r.winRate}%)</span>
                    </td>
                    <td className="text-right text-lg font-semibold tabular-nums text-gold-300">{r.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>

      <Card className="h-fit p-5 text-sm">
        <h2 className="mb-3 font-display text-lg">Как начисляются баллы</h2>
        <ul className="space-y-1.5">
          {(Object.keys(SCORE_POINTS) as ScoreReason[]).map((r) => (
            <li key={r} className="flex justify-between gap-3">
              <span className="text-zinc-400">{SCORE_LABELS[r]}</span>
              <b className={SCORE_POINTS[r] < 0 ? 'text-blood-400' : 'text-gold-300'}>
                {SCORE_POINTS[r] > 0 ? '+' : ''}
                {SCORE_POINTS[r]}
              </b>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
